import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef, Input, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { MethodApi } from 'app/api/api-request';
import { NetworkCoverage, Coverage } from 'app/models/NetworkCoverage';
import { GPS, Option, IOption } from '@co-assist/library';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import PlaceResult = google.maps.places.PlaceResult;
import { ImagesService } from 'app/services/utilities/images.service';


enum Equipment {
    REPEATER = 'repeater',
    ANTENNA = 'antenna'
}

const NO_OPT_SELECTED = 'none';

@Component({
    selector: 'app-network-check',
    templateUrl: './network-check.component.html',
    styleUrls: ['./network-check.component.scss']
})
export class NetworkCheckComponent implements OnInit {
    form: UntypedFormGroup = new UntypedFormGroup({
        primaryAddress: new UntypedFormControl(null, Validators.required),
        option: new UntypedFormControl(null, Validators.required)
    });
    loading: boolean = false;
    outdoorFlag: { class: Coverage, icon: string } = { class: undefined, icon: 'help_outline' };
    indoorFlag: { class: Coverage, icon: string } = { class: undefined, icon: 'help_outline' };
    tip: Equipment;
    @ViewChild('scrollTo') scrollTo: ElementRef;

    @Input() coverage: NetworkCoverage;
    @Input() options: Option[];
    @Output() currentSection: EventEmitter<{ section, direction }> = new EventEmitter<{ section, direction }>();
    @Output() coverageEmitter: EventEmitter<NetworkCoverage> = new EventEmitter<NetworkCoverage>();
    @Output() optionEmitter: EventEmitter<Array<Option>> = new EventEmitter<Array<Option>>();
    displayOptions: Array<DisplayOption> = [
        { id: 'RENTREPEATER2', name: Equipment.REPEATER, type: 'renting', price: 0, shipping: 0 },
        { id: 'BUYREPEATER2', name: Equipment.REPEATER, type: 'purchase', price: 0, shipping: 0 },
        { id: 'RENTANTENNA', name: Equipment.ANTENNA, type: 'renting', price: 0, shipping: 0 }
    ];
    availableOptions: Array<DisplayOption> = [];
    private optionList: Array<Option>;
    private gps: GPS;
    private country: string;
    private formatted_address: string;

    constructor(
        public imgService: ImagesService,
        private api: MethodApi,
        private toastr: ToastrService,
        private translate: TranslateService,
        private changeDetectorRef: ChangeDetectorRef
    ) { }

    get virgin() {
        const virginIcon = 'help_outline';
        return this.outdoorFlag.icon === virginIcon && this.indoorFlag.icon === virginIcon;
    }

    ngOnInit() {
        this.api.getAllOptions().subscribe((data: Array<IOption>) => {
            this.optionList = data
                .filter(elem => this.displayOptions.map(opt => opt.id).includes(elem.name))
                .map(item => new Option(item));
            const fee = data.find(o => o.name === 'FEE');
            if (fee && this.options.filter(o => o.name === 'FEE').length === 0) {
                this.options.push(new Option(fee));
            }
            this.displayOptions.forEach(elem => {
                this.optionList.forEach(opt => {
                    if (opt.type === elem.type && opt.equipment === elem.name) {
                        elem.price = opt.unitPrice;
                        elem.shipping = opt.shippingPrice;
                    }
                });
            });
            this.applyCoverageResult();
        });
    }

    placeChanged(place: google.maps.places.PlaceResult) {
        this.onAutocompleteSelected(place);
        if (place?.geometry?.location) {
            // center map
            const location = place.geometry.location;
            this.gps = new GPS({ lat: location.lat(), lng: location.lng() });
        }
        this.changeDetectorRef.detectChanges();
    }

    onAutocompleteSelected(result: PlaceResult) {
        this.formatted_address = result.formatted_address;
        result.address_components.forEach(elem => {
            if (elem.types.includes('country')) {
                this.country = elem.long_name;
            }
        });
    }

    getPlaceErrors(): string | null {
        const errors = this.form.get('primaryAddress').errors;
        if (errors?.required) {
            return this.translate.instant('REGISTRATION.FORM.ADDRESS');
        }
        if (errors?.notSelected && this.form.get('primaryAddress').touched) {
            return this.translate.instant('ADDRESS_AUTOCOMPLETE.ERROR_SELECTION');
        }
    }

    checkCoverage() {
        this.loading = true;
        if (this.form.get('primaryAddress').valid) {
            this.getCoverageEstimation(this.gps, this.country);
            this.form.get('primaryAddress').setValue(this.formatted_address);
            this.form.get('option').setValue(NO_OPT_SELECTED);
        } else {
            this.loading = false;
            this.toastr.warning(this.translate.instant('COMMON.TOAST.ADDRESS_NOT_FOUND'));
        }
    }

    buttonClick(optionId: string) {
        this.form.get('option').setValue(optionId);
    }

    continue() {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            this.coverageEmitter.emit(this.coverage);
            this.optionEmitter.emit(this.getHardwareValue());
            this.currentSection.emit({ section: 'network_check', direction: 'continue' });
        }
    }
    private getHardwareValue(): Array<Option> {
        const val = this.optionList.find(opt => opt.name === this.form.get('option').value);
        return val ? [val, ...this.options] : this.options;
    }

    private getCoverageEstimation(gpsCoord: GPS, country: string) {
        this.api.checkNetwork({ ...gpsCoord, country })
            .subscribe(
                data => {
                    this.loading = false;
                    this.coverage = new NetworkCoverage({
                        indoor: data.indoor,
                        outdoor: data.outdoor,
                        address: this.form.get('primaryAddress').value,
                        gps: gpsCoord
                    });
                    this.applyCoverageResult();
                },
                () => {
                    this.loading = false;
                    this.toastr.warning(
                        this.translate.instant('COMMON.TOAST.CONTINUE'),
                        this.translate.instant('COMMON.TOAST.SERIVCE_UNVAILABLE')
                    );
                    this.coverageEmitter.emit(this.coverage);
                    this.optionEmitter.emit(this.getHardwareValue());
                    this.currentSection.emit({ section: 'network_check', direction: 'continue' });
                }
            );
    }

    private applyCoverageResult() {
        if (this.coverage.address) {
            this.form.get('primaryAddress').setValue(this.coverage.address);
        }
        this.setFlagsAndOptions(this.coverage);
        if (!this.virgin) {
            setTimeout(() => this.scrollTo.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }), 500);
        }
    }

    /**
     * Set the outdoor and indoor flag levels according to the results
     */
    private setFlagsAndOptions({ outdoor, indoor }: NetworkCoverage) {
        this.setFlag(this.outdoorFlag, outdoor);
        if (outdoor === Coverage.KO) {
            this.setFlag(this.indoorFlag, Coverage.KO);
            this.updateAvailableOptions([this.displayOptions[2]]);
        } else {
            this.setFlag(this.indoorFlag, indoor === Coverage.KO ? Coverage.WEAK : indoor);
            if (this.indoorFlag.class === Coverage.WEAK) {
                this.updateAvailableOptions([this.displayOptions[0], this.displayOptions[1]]);
            }
        }
    }

    private updateAvailableOptions(displayOption: Array<DisplayOption>): void {
        this.availableOptions = displayOption;
        this.form.get('option').setValue(displayOption[0].id);
        this.tip = displayOption[0].name;
    }

    /**
     * Set a given flag color and icon according to passed level
     */
    private setFlag(flag: { class: string, icon: string }, coverage: Coverage) {
        flag.class = coverage;
        switch (coverage) {
            case Coverage.OK: flag.icon = 'check'; break;
            case Coverage.WEAK: flag.icon = 'warning'; break;
            case Coverage.KO: flag.icon = 'cancel'; break;
            default: flag.icon = 'help_outline';
        }
    }
}

type DisplayOption = { id: string, name: any, type: string, price: number, shipping: number };
